import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ExportPdfEvent, useAutoExportPdf } from '@/hooks/use-auto-export-pdf';
import http from '@/utils/http.service';
import { ExportAccountingDoctorAttendingToPdfViewModel } from './use-export-to-pdf.viewmodel';

export type AccoutingDoctorAttendingExportPdfEvent =
  ExportPdfEvent<'accounting-doctor-attending'>;

export const useAutoExportAccoutingDoctorAttendingPdfViewModel = ({
  autoExport,
  pageLoaded,
  ressourceLoaded,
  exportToPdfViewModel,
  accountingId,
  onSuccess,
  onFailed,
}: {
  autoExport: boolean;
  pageLoaded: boolean;
  ressourceLoaded: boolean;
  exportToPdfViewModel: ExportAccountingDoctorAttendingToPdfViewModel;
  accountingId?: string;
  onSuccess?: () => void;
  onFailed?: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const blobRef = useRef<Blob | null>(null);

  const exportAndCaptureBlob = async () => {
    const blob = await exportToPdfViewModel.export();
    blobRef.current = blob;
  };

  useAutoExportPdf({
    source: 'accounting-doctor-attending',
    autoExport,
    pageLoaded,
    ressourceLoaded,
    exportPdf: exportAndCaptureBlob,
    onSuccess: () => {
      const blob = blobRef.current;
      if (blob && blob.size > 0 && accountingId) {
        http.attendingAccountings
          .uploadPdf(accountingId, blob)
          .then(() => {
            queryClient.invalidateQueries({
              queryKey: ['attending-accountings'],
            });
            onSuccess?.();
            toast.success(t('accounting-doctor-attending.export-pdf.success'));
          })
          .catch(() => {
            onFailed?.();
            toast.error(t('accounting-doctor-attending.upload-pdf.failed'));
          });
      } else {
        onSuccess?.();
        toast.success(t('accounting-doctor-attending.export-pdf.success'));
      }
    },
    onFailed: () => {
      onFailed?.();
      toast.error(t('accounting-doctor-attending.export-pdf.failed'));
    },
  });
};
